import React, { useState, useEffect } from 'react';
import Table from '../../Components/Table';
import { DepartmentsColumns } from '../../ColumnsConfig/DepartmentsColumns';
import './../../App.css';
import { Box, Button, Chip, IconButton, InputBase, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DepartmentsDialog from '../../Modals/DepartmentsFormDialog';
import ConfirmationModal from '../../Common/ConfirmationModal';
import DepartmentsService from '../../Services/Departments';
import loaderGif from "../../Assests/loader.gif";
import { NotificationContainer, NotificationManager } from 'react-notifications';
import 'react-notifications/lib/notifications.css';

const Departments = () => {
  const [searchValue, setSearchValue] = useState('');
  const [chipValue, setChipValue] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortColumn] = useState('');
  const [order, setSortOrder] = useState('asc');
const [departmentName, setDepartmentName] = useState('');
  const validSortFields = ['service_level',];

  const fetchDepartmentsData = async (
    page = currentPage,
    size = pageSize,
    sortByField = sortBy,
    sortOrder = order,
    searchTerm = departmentName
  ) => {
    setIsLoading(true);
    try {
      const queryObj = {
        page,
        pageSize: size,
        sortBy: validSortFields.includes(sortByField) ? sortByField : 'department_code',
        order: sortOrder,
        searchTerm: searchTerm.trim() || "",
      };
  
      const response = await DepartmentsService.fetchDepartments(
        queryObj.page,
        queryObj.pageSize,
        queryObj.sortBy,
        queryObj.order,
        queryObj.searchTerm,
      );
  console.log("response.data--------------",response.data)
      const { data, totalRecords, totalPages } = response;
  
      if (Array.isArray(data)) {
        setRows(data.map(row => ({ ...row, id: row.department_id })));
        setTotalRecords(totalRecords);
        setTotalPages(totalPages);
      } else {
        throw new Error('Unexpected API response format.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.ErrorMessage || "Failed to fetch Departments data.";
      console.error('Failed to fetch Departments data:', errorMessage);
      NotificationManager.error(errorMessage, "Error", 5000);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDepartmentsData();
  }, [currentPage, pageSize]);

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchDepartmentsData(page, size, sortBy, order);
  };

  const handleSearchChange = e => setSearchValue(e.target.value);

  const handleSort = (field) => {
    let sortField = field;
  
    if (field === 'service_level') {
      sortField = 'service_level';
    }
  
    if (!validSortFields.includes(sortField)) {
      console.warn(`Invalid sort field: "${sortField}"`);
      return;
    }
  
    const newOrder = sortBy === sortField && order === 'asc' ? 'desc' : 'asc';
    setSortColumn(sortField);
    setSortOrder(newOrder);
  
    fetchDepartmentsData(currentPage, pageSize, sortField, newOrder);
  };
  
  const handleSearch = () => {
    const trimmedSearchTerm = departmentName.trim(); 
    setChipValue(trimmedSearchTerm);
    fetchDepartmentsData(1, pageSize, sortBy, order, trimmedSearchTerm); 
  };

  const handleChipDelete = async() => {
    setChipValue('');
    try{
      await fetchDepartmentsData(1, pageSize, sortBy, order, '');
  
    }catch(error){
      console.error('Error fetching SCAC data:', error);
      //NotificationManager.error("Failed to fetch SCAC data.", "Error", 5000);
    }finally{
      setDepartmentName('');
    }
  };

  const handleDialogOpen = () => {
    setInitialData(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setIsDialogOpen(false);
    }
  };

  const handleSave = async (formData) => {
      try {
        console.log("formData",formData)
          const { service_level} = formData;
          const dataToSubmit = {service_level};

          if (formData.service_level_id) {
              await DepartmentsService.updateDepartments(formData.service_level_id, dataToSubmit);
              NotificationManager.success("Departments Updated successfully.", "Success", 3000);
          } else {
              await DepartmentsService.createDepartments(dataToSubmit);
              NotificationManager.success("Departments saved successfully.", "Success", 3000);

          }

          fetchDepartmentsData(); // Refresh the table data
          setIsDialogOpen(false);
      } catch (error) {
          const errorMessage = error.response?.data?.ErrorMessage || "Failed to save Departments.";
          console.error('Error saving Departments:', errorMessage);
          NotificationManager.error(errorMessage, "Error", 5000);
      }
  };

  const handleEdit = rowData => {
    setInitialData(rowData);
    setIsDialogOpen(true);
  };

  const handleDelete = rowData => {
    setRowToDelete(rowData);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await DepartmentsService.deleteDepartments(rowToDelete.id);
      setRows(rows.filter(row => row.id !== rowToDelete.id));
      setIsDeleteModalOpen(false);
      NotificationManager.success('Departments deleted successfully.', 'Success', 3000);
    } catch (error) {
      console.error('Error deleting Departments:', error);
      NotificationManager.error('Failed to delete Departments.', 'Error', 5000);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="px-5 my-1">
      <NotificationContainer />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <Paper component="form" className="search-container" onSubmit={(e) => e.preventDefault()}>
            <InputBase
              className="search-input"
              placeholder="Search Departments"
              inputProps={{ 'aria-label': 'search Departments' }}
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
            />
            <IconButton type="button" aria-label="search" onClick={handleSearch}>
              <SearchIcon />
            </IconButton>
          </Paper>
          {chipValue && (
            <Chip
              label={chipValue}
              onDelete={handleChipDelete}
              deleteIcon={<CloseIcon />}
              className="chip-style"
            />
          )}
        </Box>
      </Box>

      <Table
        rows={rows}
        columns={DepartmentsColumns(handleEdit, handleDelete)}
        className="table-header"
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        totalPages={totalPages}
        getRowId={row => row.id}
        onSort={handleSort}
      />

      {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.8)"
          zIndex={1}
        >
          <img src={loaderGif} alt="Loading..." width={50} height={50} />
        </Box>
      )}

      <DepartmentsDialog
        open={isDialogOpen}
        handleClose={handleDialogClose}
        handleSave={handleSave}
        initialData={initialData}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        message="Are you sure you want to delete this ServiceLvele?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default Departments;
